import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { isDefined } from "@recursyve/nice-ts-utils";
import { catchError, EMPTY, map, Observable } from "rxjs";
import { toFieldStringArray } from "../../../../../common/utils/object-paths";
import {
    sanitizeDirectusArray,
    sanitizeDirectusObject,
    sanitizeDirectusRelationArray
} from "../../../../../common/utils/sanitize-directus-object";
import { ApiService } from "../../../../api/packbo/api.service";
import { readItemsAdBlock } from "../../../../api/packbo/fn/items/read-items-ad-block";
import { readItemsCertifications } from "../../../../api/packbo/fn/items/read-items-certifications";
import { readItemsProductCategories } from "../../../../api/packbo/fn/items/read-items-product-categories";
import { readItemsSuppliers } from "../../../../api/packbo/fn/items/read-items-suppliers";
import { ItemsHeaderBlock } from "../../../../api/packbo/models/items-header-block";
import { ItemsProductCategories } from "../../../../api/packbo/models/items-product-categories";
import { ItemsSuppliers } from "../../../../api/packbo/models/items-suppliers";
import { setLoadingState } from "../../../../rxjs/set-loading-state.operator";
import { CategoryStore } from "./category.store";

type SearchOptions = {
    subcategoryId: number;
    searchTerm: string;
    postalCode: string;
    distance: string;
    page: number;
    certifications: number[];
    isEcoFriendly: boolean;
};

@Injectable()
export class CategoryService {
    private readonly store = inject(CategoryStore);
    private readonly apiService = inject(ApiService);
    private readonly http = inject(HttpClient);

    public reset(): void {
        this.store.resetPagination();
        this.store.setSuppliers([]);
        this.store.setIsEcoFriendly(false);
    }

    public getSuppliersBySubcategory(options: Pick<SearchOptions, "subcategoryId" | "page">): Observable<void> {
        const { subcategoryId, page = 1 } = options;

        if (page === 1) {
            this.store.resetPagination();
        }

        const supplierFields = this.getSupplierFields();
        const suppliersFilter = JSON.stringify({
            product_categories_refs: {
                product_categories_id: {
                    id: { _eq: subcategoryId }
                }
            },
            is_active: { _eq: true }
        });
        const limit = this.store.pagination().itemsPerPage;
        const offset = (page - 1) * limit;
        const sort = ["-package_ref.is_showcase_membership", "package_ref.hide_supplier_package", "name"];

        return this.apiService
            .invoke(readItemsSuppliers, {
                fields: supplierFields,
                filter: suppliersFilter,
                limit,
                offset,
                sort,
                meta: "filter_count"
            })
            .pipe(
                setLoadingState(this.store, "loading", { minimumDuration: 500 }),
                map((response) => {
                    const suppliers = response.data ?? [];
                    const totalItems = response.meta?.filter_count ?? suppliers.length;
                    const totalPages = Math.ceil(totalItems / limit);

                    this.store.setSuppliers(suppliers);
                    this.store.setPagination({
                        currentPage: page,
                        totalPages
                    });
                }),
                catchError(() => EMPTY)
            );
    }

    public fetchCategory(slug: string): Observable<void> {
        const categoryFields = toFieldStringArray({
            id: null,
            icon: null,
            image: null,
            alternative_image: null,
            parent_ref: null,
            translations_refs: {
                languages_code: null,
                title: null,
                description: null,
                slug: null
            }
        });
        const categoryFilter = JSON.stringify({
            translations_refs: {
                slug: { _eq: slug }
            }
        });

        return this.apiService
            .invoke(readItemsProductCategories, { fields: categoryFields, filter: categoryFilter })
            .pipe(
                map((response) => {
                    const category = response.data?.[0] ?? null;
                    if (category) {
                        const headerBlock = this.toItemsHeaderBlock(category);
                        this.store.setHeaderBlock(headerBlock);
                        this.store.setCategory(category);
                    }
                }),
                catchError(() => EMPTY)
            );
    }

    public fetchSubcategories(slug: string): Observable<void> {
        const subcategoriesFields = toFieldStringArray({
            id: null,
            icon: null,
            image: null,
            parent_ref: {
                id: null,
                icon: null,
                image: null,
                alternative_image: null,
                parent_ref: null,
                translations_refs: {
                    languages_code: null,
                    title: null,
                    description: null,
                    slug: null
                }
            },
            translations_refs: {
                languages_code: null,
                title: null,
                description: null,
                slug: null
            }
        });
        const subcategoriesFilter = JSON.stringify({
            parent_ref: {
                translations_refs: {
                    slug: { _eq: slug }
                }
            }
        });

        return this.apiService
            .invoke(readItemsProductCategories, { fields: subcategoriesFields, filter: subcategoriesFilter })
            .pipe(
                map((response) => {
                    const filteredSubcategories = this.filterSubcategoriesByAlphabeticalOrder(response.data ?? []);
                    this.store.setSubcategories(filteredSubcategories);
                    this.store.setFilteredSubcategories(filteredSubcategories);
                }),
                catchError(() => EMPTY)
            );
    }

    public fetchAds(): Observable<void> {
        const adsFields = toFieldStringArray({
            id: null,
            ad_type: null,
            ads_refs: {
                ads_id: {
                    id: null,
                    image: null,
                    url: null
                }
            },
            is_category_ad: null
        });

        const adsFilter = JSON.stringify({
            is_category_ad: { _eq: true }
        });

        return this.apiService.invoke(readItemsAdBlock, { fields: adsFields, filter: adsFilter }).pipe(
            map((ads) => this.store.setAdBlocks(ads.data ?? [])),
            catchError(() => EMPTY)
        );
    }

    public fetchCertifications(): Observable<void> {
        const certificationsFields = toFieldStringArray({
            id: null,
            logo: null,
            name: null
        });

        return this.apiService.invoke(readItemsCertifications, { fields: certificationsFields }).pipe(
            map((certifications) => this.store.setItemsCertifications(certifications.data ?? [])),
            catchError(() => EMPTY)
        );
    }

    public getSubcategoriesByName(searchValue: string): void {
        const subcategories = this.store.subcategories();
        const filteredSubcategories = subcategories.filter((subcategory) => {
            const titles = sanitizeDirectusRelationArray(subcategory.translations_refs, "title").filter(isDefined);
            return titles.some((title) => title.toLowerCase().includes(searchValue));
        });

        this.store.setFilteredSubcategories(filteredSubcategories);
    }

    public getSuppliersBySubcategoryId(subcategoryId: number, page = 1): Observable<void> {
        if (page === 1) {
            this.store.resetPagination();
        }

        const supplierFields = this.getSupplierFields();
        const suppliersFilter = JSON.stringify({
            product_categories_refs: {
                product_categories_id: {
                    id: { _eq: subcategoryId }
                }
            },
            is_active: { _eq: true }
        });
        const limit = this.store.pagination().itemsPerPage;
        const offset = (page - 1) * limit;
        const sort = ["-package_ref.is_showcase_membership", "package_ref.hide_supplier_package", "name"];

        return this.apiService
            .invoke(readItemsSuppliers, {
                fields: supplierFields,
                filter: suppliersFilter,
                limit,
                offset,
                sort,
                meta: "filter_count"
            })
            .pipe(
                setLoadingState(this.store, "loading", { minimumDuration: 500 }),
                map((response) => {
                    const suppliers = response.data ?? [];
                    const totalItems = response.meta?.filter_count ?? 0;
                    const totalPages = Math.ceil(totalItems / limit);

                    this.store.setSuppliers(suppliers);
                    this.store.setPagination({
                        currentPage: page,
                        totalPages
                    });
                }),
                catchError(() => EMPTY)
            );
    }

    public searchSuppliers(options: SearchOptions): Observable<void> {
        const { postalCode, distance, page = 1 } = options;

        if (page === 1) {
            this.store.resetPagination();
        }

        const hasLocationSearch = postalCode?.trim() && distance?.trim();
        if (hasLocationSearch) {
            return this.performLocationBasedSearch(options);
        } else {
            return this.performTextSearch(options);
        }
    }

    private performLocationBasedSearch(
        options: Pick<
            SearchOptions,
            "subcategoryId" | "searchTerm" | "postalCode" | "distance" | "page" | "certifications" | "isEcoFriendly"
        >
    ): Observable<void> {
        const { subcategoryId, searchTerm, postalCode, distance, page, certifications, isEcoFriendly } = options;

        const customEndpointUrl = "/supplier-search/by-location";
        const params = new URLSearchParams({
            searchTerm: searchTerm || "",
            postalCode,
            distance,
            page: page.toString(),
            limit: this.store.pagination().itemsPerPage.toString(),
            isEcoFriendly: isEcoFriendly.toString()
        });

        if (subcategoryId) {
            params.append("subcategoryId", subcategoryId.toString());
        }

        certifications.forEach((certId) => {
            params.append("certifications", certId.toString());
        });

        const fullUrl = `${this.apiService.rootUrl}${customEndpointUrl}?${params}`;

        type CustomEndpointResponse = {
            data?: ItemsSuppliers[];
            meta?: { current_page?: number; total_pages?: number; filter_count?: number };
        };

        return this.http.get<CustomEndpointResponse>(fullUrl).pipe(
            setLoadingState(this.store, "loading", { minimumDuration: 500 }),
            map((response) => {
                const suppliers = response.data ?? [];
                const meta = response.meta ?? {};

                if (suppliers.length === 0) {
                    this.store.setSupplierImagesLoading(false);
                }

                this.store.setSuppliers(suppliers);
                this.store.setPagination({
                    currentPage: meta.current_page || page,
                    totalPages: meta.total_pages || 1
                });
            }),
            catchError(() => {
                this.store.setSupplierImagesLoading(false);
                this.store.setSuppliers([]);
                return EMPTY;
            })
        );
    }

    private performTextSearch(
        options: Pick<SearchOptions, "subcategoryId" | "searchTerm" | "page" | "certifications" | "isEcoFriendly">
    ): Observable<void> {
        const { subcategoryId, searchTerm, page, certifications, isEcoFriendly } = options;

        const supplierFields = this.getSupplierFields();
        const supplierFilter = this.getSupplierFilter(subcategoryId, searchTerm, certifications, isEcoFriendly);
        const limit = this.store.pagination().itemsPerPage;
        const offset = (page - 1) * limit;
        const sort = ["-package_ref.is_showcase_membership", "package_ref.hide_supplier_package", "name"];

        return this.apiService
            .invoke(readItemsSuppliers, {
                fields: supplierFields,
                filter: supplierFilter,
                limit,
                offset,
                sort,
                meta: "filter_count"
            })
            .pipe(
                setLoadingState(this.store, "loading", { minimumDuration: 500 }),
                map((response) => {
                    const suppliers = response.data ?? [];
                    const totalItems = response.meta?.filter_count ?? 0;
                    const totalPages = Math.ceil(totalItems / limit);

                    this.store.setSuppliers(suppliers);
                    this.store.setPagination({
                        currentPage: page,
                        totalPages
                    });
                }),
                catchError(() => {
                    this.store.setSupplierImagesLoading(false);
                    this.store.setSuppliers([]);
                    return EMPTY;
                })
            );
    }

    private getSupplierFields(): string[] {
        return toFieldStringArray({
            id: null,
            name: null,
            banner: null,
            certifications_refs: {
                certifications_id: {
                    id: null,
                    logo: null,
                    name: null,
                    website: null,
                    translations_refs: {
                        languages_code: null,
                        name: null
                    }
                }
            },
            contact_ref: {
                id: null,
                email: null,
                phone_number: null,
                first_name: null,
                last_name: null,
                coord_ref: {
                    id: null,
                    address1: null,
                    address2: null,
                    locality: null,
                    postal_code: null
                }
            },
            coord_ref: {
                id: null,
                address1: null,
                address2: null,
                locality: null,
                postal_code: null
            },
            product_categories_refs: {
                product_categories_id: {
                    id: null,
                    image: null,
                    parent_ref: {
                        id: null,
                        image: null
                    }
                }
            },
            email: null,
            is_active: null,
            logo: null,
            package_ref: {
                id: null,
                package_color: null,
                name: null,
                hide_supplier_package: null,
                is_showcase_membership: null,
                package_text_color: null,
                translations_refs: {
                    languages_code: null,
                    title: null
                }
            },
            phone_number: null,
            status: null,
            website_url: null,
            translations_refs: {
                languages_code: null,
                summary: null,
                description: null,
                slug: null
            },
            is_eco_friendly: null,
            files_refs: {
                directus_files_id: {
                    id: null,
                    type: null
                },
                id: null
            }
        });
    }

    private getSupplierFilter(
        subcategoryId: number,
        searchTerm: string,
        certifications: number[],
        isEcoFriendly: boolean
    ): string {
        const filter = {
            is_active: { _eq: true }
        } as Record<string, unknown>;

        if (searchTerm && searchTerm.trim() !== "") {
            filter["_or"] = [
                {
                    name: { _contains: searchTerm }
                },
                {
                    keywords: { _contains: searchTerm }
                },
                {
                    product_categories_refs: {
                        product_categories_id: {
                            translations_refs: {
                                title: { _contains: searchTerm }
                            }
                        }
                    }
                }
            ];
        }

        if (subcategoryId) {
            filter["product_categories_refs"] = {
                product_categories_id: {
                    id: { _eq: subcategoryId }
                }
            };
        }

        if (certifications.length) {
            filter["certifications_refs"] = {
                certifications_id: {
                    id: { _in: certifications }
                }
            };
        }

        if (isEcoFriendly) {
            filter["is_eco_friendly"] = { _eq: true };
        }

        return JSON.stringify(filter);
    }

    private toItemsHeaderBlock(category: ItemsProductCategories): ItemsHeaderBlock | null {
        const itemProductCategory = sanitizeDirectusObject(category);
        if (!itemProductCategory) {
            return null;
        }

        const translations = sanitizeDirectusArray(itemProductCategory.translations_refs);
        if (translations.length === 0) {
            return null;
        }

        const headerTranslations = translations.map((translation) => ({
            languages_code: translation.languages_code,
            title: translation.title,
            subtitle: translation.description
        }));

        const firstTranslation = translations[0];

        return {
            name: firstTranslation.title,
            type: "full",
            translations_refs: headerTranslations,
            image: itemProductCategory.alternative_image
        };
    }

    private filterSubcategoriesByAlphabeticalOrder(subcategories: ItemsProductCategories[]): ItemsProductCategories[] {
        return subcategories.sort((a, b) => {
            const firstTitle = sanitizeDirectusRelationArray(a.translations_refs, "title").filter(isDefined);
            const secondTitle = sanitizeDirectusRelationArray(b.translations_refs, "title").filter(isDefined);

            const firstSubcategory = firstTitle[0];
            const secondSubcategory = secondTitle[0];

            return firstSubcategory.localeCompare(secondSubcategory);
        });
    }
}
